import React, { useEffect, useState } from 'react';
import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_API_KEY = 'phc_dfLSIQAGgA350UPP9HEKtSkiWIRc2E3HCIUVSs44zWo';
const POSTHOG_API_HOST = 'https://eu.i.posthog.com';


// Styling constants
const CONTAINER_CLASSES = "fixed bottom-4 right-4 left-4 md:left-auto z-50";
const BANNER_CLASSES = "bg-white w-full md:max-w-md rounded-xl shadow-2xl ml-auto";
const BUTTON_PRIMARY_CLASSES = "bg-black text-white px-6 py-3 rounded text-sm";
const BUTTON_OUTLINE_CLASSES = "bg-white border border-black text-black px-6 py-3 rounded text-sm";
const BUTTON_SECONDARY_CLASSES = "text-black font-medium text-sm hover:underline";
const TOGGLE_BUTTON_CLASSES = "text-xs mr-2 text-gray-400 font-bold cursor-pointer w-4";

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

// ------------------------
// Utility Functions
// ------------------------

export const getDefaultPreferences = () => ({
  essential: true,
  analytics: false,
  advertising: false
});

export const hasUserMadeChoice = () => {
  if (typeof window === 'undefined') return false;
  const consentCookie = localStorage.getItem('consent_preferences');
  return consentCookie !== null;
};

export const hasConsent = (type) => {
  if (typeof window === 'undefined') return false;
  try {
    const consentCookie = localStorage.getItem('consent_preferences');
    if (!consentCookie) return false;
    const preferences = JSON.parse(consentCookie);
    return preferences[type] === true;
  } catch (error) {
    console.error('Error reading consent preferences:', error);
    return false;
  }
};

export const getAllConsents = () => {
  if (typeof window === 'undefined') return getDefaultPreferences();
  try {
    const consentCookie = localStorage.getItem('consent_preferences');
    if (!consentCookie) return getDefaultPreferences();
    return { ...getDefaultPreferences(), ...JSON.parse(consentCookie) };
  } catch (error) {
    console.error('Error reading consent preferences:', error);
    return getDefaultPreferences();
  }
};

export const saveConsents = (preferences) => {
  if (typeof window === 'undefined') return;
  const updatedPreferences = {
    ...preferences,
    essential: true,
    timestamp: new Date().toISOString()
  };
  localStorage.setItem('consent_preferences', JSON.stringify(updatedPreferences));
  updatePosthogConsent(updatedPreferences.analytics);
  updateGTMConsent(updatedPreferences.analytics, updatedPreferences.advertising);
};

export const initializePosthog = (forceInit = false) => {
  if (typeof window === 'undefined') return false;
  const analyticsEnabled = hasConsent('analytics');
  const isInitialized = Boolean(window.posthog && typeof window.posthog.init === 'function' && window.posthog.config && window.posthog.config.api_host === POSTHOG_API_HOST);
  
  if ((analyticsEnabled || forceInit) && !isInitialized) {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_API_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false,
      persistence: 'memory',
      autocapture: false
    });
    if (analyticsEnabled) {
      posthog.opt_in_capturing({
        persistence_type: 'localStorage',
        enable_persistence: true
      });
      posthog.capture('$pageview');
    } else {
      posthog.opt_out_capturing();
    }
    return true;
  } else if (isInitialized) {
    updatePosthogConsent(analyticsEnabled);
    return true;
  }
  return false;
};

export const updatePosthogConsent = (enabled) => {
  if (typeof window === 'undefined' || !window.posthog) return;
  if (enabled) {
    posthog.opt_in_capturing({
      persistence_type: 'localStorage', 
      enable_persistence: true
    });
    posthog.capture('$pageview');
  } else {
    posthog.opt_out_capturing();
  }
};

export const updateGTMConsent = (analytics, advertising) => {
  if (typeof window === 'undefined') return;
  
  if (window.gtag) {
    const consentUpdatePayload = {
      'analytics_storage': analytics ? 'granted' : 'denied',
      'ad_storage': advertising ? 'granted' : 'denied',
      'ad_user_data': advertising ? 'granted' : 'denied', // Связано с согласием на рекламу
      'ad_personalization': advertising ? 'granted' : 'denied', // Связано с согласием на рекламу
      'personalization_storage': analytics ? 'granted' : 'denied', // Если персонализация сайта связана с аналитикой
      // 'functionality_storage' и 'security_storage' обычно не обновляются здесь,
      // так как они установлены в 'default' и редко меняются пользователем.
      // Если они могут меняться, их нужно включить.
      // Для примера, если они всегда granted, их можно не передавать в update:
      // 'functionality_storage': 'granted',
      // 'security_storage': 'granted'
    };

    window.gtag('consent', 'update', consentUpdatePayload);
    
    if (isDevelopment) {
      console.log('[Consent] GTM Consent Updated:', consentUpdatePayload);
    }
  } else {
    if (isDevelopment) {
      console.warn('GTM gtag API not available for consent update');
    }
  }
};

// ------------------------
// React Component
// ------------------------

export function ConsentManager() {
  const [consentShown, setConsentShown] = useState(false);
  const [debugVisible, setDebugVisible] = useState(false); // GTM_ID потребуется здесь для дебаггера
  const [showSettings, setShowSettings] = useState(false);
  const [consentState, setConsentState] = useState({
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  
  const [settingsViewPreferences, setSettingsViewPreferences] = useState({
    analytics: false,
    advertising: false
  });
  
  const [expandedSections, setExpandedSections] = useState({
    analytics: false,
    advertising: false
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const consentCookie = localStorage.getItem('consent_preferences');
    if (consentCookie) {
      try {
        const savedConsent = JSON.parse(consentCookie);
        setConsentShown(false); // Не показываем баннер, если выбор уже сделан
        setSettingsViewPreferences({
          analytics: savedConsent.analytics || false,
          advertising: savedConsent.advertising || false
        });
        initializePosthog(); // Инициализируем PostHog на основе сохраненных настроек
        // Первоначальный вызов updateGTMConsent УБРАН ОТСЮДА, так как он теперь в MyApp.js
      } catch (e) {
        console.error('Error reading saved consent settings:', e);
        setConsentShown(true); // Показываем баннер при ошибке
      }
    } else {
      // Если куки согласия нет, показываем баннер
      setConsentShown(true);
      // Инициализируем PostHog (скорее всего, в состоянии opt-out по умолчанию)
      initializePosthog(); 
    }
    
    // Дебаггер GTM_ID из MyApp.js
    // const GTM_ID_FOR_DEBUGGER = 'GTM-KC5G5WLD'; // Можно передать как prop или захардкодить, если один
    if (isDevelopment) {
      // setTimeout(() => setupConsentDebugger(GTM_ID_FOR_DEBUGGER), 1000); // Передаем GTM_ID
    }
  }, []); 
  

  
  const updateConsent = (analytics, advertising) => {
    saveConsents({
      essential: true,
      analytics,
      advertising
    });
    if (analytics) {
      initializePosthog(true); // Принудительная инициализация или opt-in
    } else if (window.posthog) {
      posthog.opt_out_capturing();
    }
    setConsentShown(false);
    setShowSettings(false);
  };
  
  const getStatusLabel = (status) => {
    return status === 'granted' ? '✅ Allowed' : '❌ Denied';
  };
  
  const handleAcceptAll = () => updateConsent(true, true);
  const handleRejectAll = () => updateConsent(false, false);
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const handleToggleCookie = (type) => {
    setSettingsViewPreferences(prev => ({ ...prev, [type]: !prev[type] }));
  };
  
  const handleConfirmSettings = () => {
    updateConsent(settingsViewPreferences.analytics, settingsViewPreferences.advertising);
  };
  
  const getToggleSwitchClasses = (enabled) => ({
    container: `w-10 h-5 rounded-full relative ${enabled ? 'bg-black' : 'bg-gray-300'} cursor-pointer`,
    slider: `absolute w-4 h-4 rounded-full bg-white top-0.5 transition-all ${enabled ? 'right-0.5' : 'left-0.5'}`
  });
  
  const renderCookieSection = (type, label, description) => {
    const isExpanded = expandedSections[type];
    const isEnabled = settingsViewPreferences[type];
    const toggleClasses = getToggleSwitchClasses(isEnabled);
    
    return (
      <div className="py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center cursor-pointer" onClick={() => toggleSection(type)}>
            <button className={TOGGLE_BUTTON_CLASSES}>{isExpanded ? '−' : '+'}</button>
            <span className="pl-2 text-sm">{label}</span>
          </div>
          <div className="flex items-center">
            <span className={`mr-2 text-sm ${isEnabled ? 'text-black' : 'text-gray-400'}`}>{isEnabled ? 'Enabled' : 'Disabled'}</span>
            <div onClick={() => handleToggleCookie(type)} className={toggleClasses.container}>
              <div className={toggleClasses.slider} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}></div>
            </div>
          </div>
        </div>
        {isExpanded && (<div className="mt-2 text-gray-600 pl-8 text-sm">{description}</div>)}
      </div>
    );
  };

  return (
    <>
      {consentShown && !showSettings && (
        <div className={CONTAINER_CLASSES}><div className={BANNER_CLASSES}><div className="p-6">
          <h2 className="text-lg font-bold mb-1">Cookies managing</h2>
          <p className="mb-6 text-sm">We use cookies to provide the best site experience. By continuing to use the site, you consent to our cookie policy.</p>
          <div className="flex gap-4">
            <button onClick={handleAcceptAll} className={BUTTON_PRIMARY_CLASSES}>Accept All</button>
            <button onClick={handleRejectAll} className={BUTTON_OUTLINE_CLASSES}>Reject All</button>
            <button onClick={() => setShowSettings(true)} className={BUTTON_SECONDARY_CLASSES}>Cookie Settings</button>
          </div>
        </div></div></div>
      )}
      
      {consentShown && showSettings && (
        <div className={CONTAINER_CLASSES}><div className={BANNER_CLASSES}>
          <div className="flex items-center p-4 pl-6 mb-0">
            <button onClick={() => setShowSettings(false)} className="text-gray-500 mr-2">←</button>
            <span className="text-xs text-gray-500">Cookies managing</span>
          </div>
          <div className="px-6 pt-1 pb-6">
            <h2 className="text-lg font-bold mb-4">Cookie Settings</h2>
            <p className="mb-2 text-sm">Cookies necessary for the correct operation of the site are always enabled. Other cookies are configurable.</p>
            <div className="space-y-4">
              <div className="py-4"><div className="flex justify-between items-center">
                <div className="flex items-center"><span className="pl-2 text-sm">Essential cookies</span></div>
                <span className="text-blue-500 font-bold text-sm">Always Enabled</span>
              </div></div>
              {renderCookieSection('analytics', 'Analytics cookies', 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They allow us to measure and improve the performance of our site.')}
              {renderCookieSection('advertising', 'Advertising cookies', 'These cookies are used to track visitors across websites. They are set to display targeted advertisements based on your interests and online behavior. They also help measure the effectiveness of advertising campaigns.')}
            </div>
            <div className="mt-8"><button onClick={handleConfirmSettings} className={BUTTON_PRIMARY_CLASSES}>Confirm</button></div>
          </div>
        </div></div>
      )}
      
      {/* Debug button and panel - GTM_ID for debugger needs to be accessible here */}
      {/* Consider passing GTM_ID as a prop if you keep the debugger active */}
    </>
  );
}


export default {
  hasConsent,
  hasUserMadeChoice,
  getAllConsents,
  saveConsents,
  initializePosthog,
  updatePosthogConsent,
  updateGTMConsent,
  ConsentManager,
};